import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Divider,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import {
    Person as PersonIcon,
    Security as SecurityIcon,
    History as HistoryIcon,
    Visibility,
    VisibilityOff,
    ArrowBack,
} from '@mui/icons-material';
import {
    useGetIdentity,
    useNotify,
    useTranslate,
    Title,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { apiClient } from '../../utils/apiClient';
import { translateServerMessage } from '../../utils/translateServerMessage';

type FieldErrorMap = Record<string, string>;

const mapProfileBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

export const MyProfile = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data: identity, isLoading: identityLoading } = useGetIdentity();
    const notify = useNotify();
    const translate = useTranslate();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState<any>(null);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordFieldErrors, setPasswordFieldErrors] = useState<FieldErrorMap>({});
    const [totpEnabled, setTotpEnabled] = useState(false);
    const [auditLogs, setAuditLogs] = useState<any[]>([]);
    const [auditLoading, setAuditLoading] = useState(false);

    // 2FA Dialog states
    const [show2FASetupDialog, setShow2FASetupDialog] = useState(false);
    const [show2FADisableDialog, setShow2FADisableDialog] = useState(false);
    const [totpUri, setTotpUri] = useState('');
    const [totpSecret, setTotpSecret] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationCodeError, setVerificationCodeError] = useState('');
    const [disablePassword, setDisablePassword] = useState('');
    const [disablePasswordError, setDisablePasswordError] = useState('');

    // Shared helper: convert apiClient error shape to field-error map + summary notify.
    const extractFieldErrors = (error: any, fallbackKey: string): FieldErrorMap => {
        const apiError = error?.body || error;
        const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
        const fieldErrors: FieldErrorMap = {};
        if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
                const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                const translated = translateServerMessage(rawMsg, translate);
                const field = mapProfileBackendPath(path);
                fieldErrors[field] = translated;
            }
        }

        const headerKey = apiError?.errorCode || apiError?.error_code
            ? `errors.${apiError.errorCode || apiError.error_code}`
            : fallbackKey;
        const header = translate(headerKey, {
            _: apiError?.message || translate(fallbackKey, { _: 'Error' })
        });

        const summaryLines: string[] = [];
        for (const [, msg] of Object.entries(fieldErrors)) {
            summaryLines.push(`\u2022 ${msg}`);
        }

        if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
                type: 'error',
                multiLine: true,
                autoHideDuration: 10000,
            });
        } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
        }

        return fieldErrors;
    };
    
    // Load user data and audit logs
    React.useEffect(() => {
        if (identity?.id) {
            loadUserData();
            loadAuditLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identity?.id]);
    
    const loadUserData = async () => {
        try {
            if (!identity?.id) return;
            const response = await apiClient.get<{ user: any }>('/api/backend/auth/me');
            setUserData(response.user);
            setTotpEnabled(response.user.totpEnabled || false);
        } catch (error) {
            console.error('Failed to load user data:', error);
        }
    };
    
    const loadAuditLogs = async () => {
        setAuditLoading(true);
        try {
            const response = await apiClient.get<{ data: any[] }>(
                '/api/backend/auth/me/audit-logs?page=1&perPage=10'
            );
            setAuditLogs(response.data || []);
        } catch (error) {
            console.error('Failed to load audit logs:', error);
        } finally {
            setAuditLoading(false);
        }
    };
    
    const handlePasswordChange = async () => {
        setPasswordError('');
        setPasswordFieldErrors({});

        // Validate passwords (client-side)
        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            setPasswordError(translate('users.profile.passwordRequired'));
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordFieldErrors({ confirmPassword: translate('users.profile.passwordMismatch') });
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setPasswordFieldErrors({ newPassword: translate('users.profile.passwordTooShort') });
            return;
        }

        setLoading(true);
        try {
            await apiClient.post('/api/backend/auth/password/change', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });

            notify(translate('users.profile.passwordUpdated'), { type: 'success' });
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
        } catch (error: any) {
            const fieldErrors = extractFieldErrors(error, 'users.profile.passwordUpdateFailed');
            setPasswordFieldErrors(fieldErrors);
        } finally {
            setLoading(false);
        }
    };
    
    const handleToggle2FA = async () => {
        if (totpEnabled) {
            // Show disable dialog
            setDisablePasswordError('');
            setShow2FADisableDialog(true);
        } else {
            // Setup 2FA - get QR code
            setLoading(true);
            try {
                const response = await apiClient.post<{ qrCode: string; secret: string; uri: string }>('/api/backend/auth/2fa/setup');
                setTotpUri(response.uri);
                setTotpSecret(response.secret);
                setVerificationCodeError('');
                setShow2FASetupDialog(true);
            } catch (error: any) {
                extractFieldErrors(error, 'users.profile.2faSetupFailed');
            } finally {
                setLoading(false);
            }
        }
    };
    
    const handleVerify2FA = async () => {
        setVerificationCodeError('');

        if (!verificationCode || verificationCode.length !== 6) {
            setVerificationCodeError(translate('users.profile.2faCodeRequired'));
            return;
        }

        setLoading(true);
        try {
            await apiClient.post('/api/backend/auth/2fa/verify', { code: verificationCode });
            notify(translate('users.profile.2faEnabled'), { type: 'success' });
            setTotpEnabled(true);
            setShow2FASetupDialog(false);
            setVerificationCode('');
            setTotpUri('');
            setTotpSecret('');
            // Reload user data to get updated status
            loadUserData();
        } catch (error: any) {
            const fieldErrors = extractFieldErrors(error, 'users.profile.2faVerifyFailed');
            // The 'code' field is the primary error target. Fall back to any other field error.
            const codeErr = fieldErrors.code || Object.values(fieldErrors)[0] || '';
            if (codeErr) {
                setVerificationCodeError(codeErr);
            } else {
                // No field error — show generic inline error too so the user knows input was wrong.
                const apiError = error?.body || error;
                const codeHint = apiError?.errorCode || apiError?.error_code
                    ? translate(`errors.${apiError.errorCode || apiError.error_code}`, {
                          _: translate('users.profile.2faVerifyFailed'),
                      })
                    : translate('users.profile.2faVerifyFailed');
                setVerificationCodeError(codeHint);
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleDisable2FA = async () => {
        setDisablePasswordError('');

        if (!disablePassword) {
            setDisablePasswordError(translate('users.profile.passwordRequired'));
            return;
        }

        setLoading(true);
        try {
            await apiClient.post('/api/backend/auth/2fa/disable', { password: disablePassword });
            notify(translate('users.profile.2faDisabled'), { type: 'success' });
            setTotpEnabled(false);
            setShow2FADisableDialog(false);
            setDisablePassword('');
            // Reload user data to get updated status
            loadUserData();
        } catch (error: any) {
            const fieldErrors = extractFieldErrors(error, 'users.profile.2faDisableFailed');
            const pwErr = fieldErrors.password || Object.values(fieldErrors)[0] || '';
            if (pwErr) {
                setDisablePasswordError(pwErr);
            } else {
                const apiError = error?.body || error;
                const hint = apiError?.errorCode || apiError?.error_code
                    ? translate(`errors.${apiError.errorCode || apiError.error_code}`, {
                          _: translate('users.profile.2faDisableFailed'),
                      })
                    : translate('users.profile.2faDisableFailed');
                setDisablePasswordError(hint);
            }
        } finally {
            setLoading(false);
        }
    };
    
    if (identityLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Box>
            <Title title={translate('users.profile.title')} />
            
            {/* Header with Back Button */}
            <Box
                sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    mb: { xs: 2, sm: 3 },
                    boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <IconButton
                    onClick={() => navigate('/')}
                    sx={{
                        backgroundColor: '#F5F5F5',
                        '&:hover': {
                            backgroundColor: '#E0E0E0',
                        },
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    {translate('users.profile.title')}
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 3 } }}>
                {/* Personal Information */}
                <Box sx={{ flex: 1 }}>
                    <Card sx={{ boxShadow: '0 12px 40px rgba(0,0,0,0.12)', height: '100%', overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" alignItems="center" mb={3}>
                                <PersonIcon sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('users.profile.personalInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.backend-users.fields.username')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {userData?.username || identity?.username || identity?.email}
                                    </Typography>
                                </Box>
                                
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.backend-users.fields.email')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {userData?.email || identity?.email}
                                    </Typography>
                                </Box>
                                
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.backend-users.fields.roleId')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {identity?.role?.name || 'Administrator'}
                                    </Typography>
                                </Box>
                                
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('users.profile.memberSince')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {userData?.createdAt ? format(new Date(userData.createdAt), 'PPP') : '-'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                
                {/* Security Settings */}
                <Box sx={{ flex: 1 }}>
                    <Card sx={{ boxShadow: '0 12px 40px rgba(0,0,0,0.12)', height: '100%', overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" alignItems="center" mb={3}>
                                <SecurityIcon sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('users.profile.securitySettings')}
                                </Typography>
                            </Box>
                            
                            {/* Change Password */}
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                {translate('users.profile.changePassword')}
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    type={showPasswords.current ? 'text' : 'password'}
                                    label={translate('users.profile.currentPassword')}
                                    value={passwordData.currentPassword}
                                    onChange={(e) => {
                                        setPasswordData({ ...passwordData, currentPassword: e.target.value });
                                        if (passwordFieldErrors.currentPassword) {
                                            setPasswordFieldErrors({ ...passwordFieldErrors, currentPassword: '' });
                                        }
                                    }}
                                    margin="dense"
                                    error={!!passwordFieldErrors.currentPassword}
                                    helperText={passwordFieldErrors.currentPassword || undefined}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                                    edge="end"
                                                >
                                                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    type={showPasswords.new ? 'text' : 'password'}
                                    label={translate('users.profile.newPassword')}
                                    value={passwordData.newPassword}
                                    onChange={(e) => {
                                        setPasswordData({ ...passwordData, newPassword: e.target.value });
                                        if (passwordFieldErrors.newPassword) {
                                            setPasswordFieldErrors({ ...passwordFieldErrors, newPassword: '' });
                                        }
                                    }}
                                    margin="dense"
                                    error={!!passwordFieldErrors.newPassword}
                                    helperText={passwordFieldErrors.newPassword || undefined}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                                    edge="end"
                                                >
                                                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    type={showPasswords.confirm ? 'text' : 'password'}
                                    label={translate('users.profile.confirmPassword')}
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => {
                                        setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                                        if (passwordFieldErrors.confirmPassword) {
                                            setPasswordFieldErrors({ ...passwordFieldErrors, confirmPassword: '' });
                                        }
                                    }}
                                    margin="dense"
                                    error={!!passwordFieldErrors.confirmPassword}
                                    helperText={passwordFieldErrors.confirmPassword || undefined}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                                    edge="end"
                                                >
                                                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {passwordError && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        {passwordError}
                                    </Alert>
                                )}
                                
                                <Button
                                    variant="contained"
                                    onClick={handlePasswordChange}
                                    disabled={loading}
                                    sx={{ mt: 2 }}
                                >
                                    {translate('users.profile.updatePassword')}
                                </Button>
                            </Box>
                            
                            <Divider sx={{ my: 3 }} />
                            
                            {/* 2FA Settings */}
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                {translate('users.profile.twoFactorAuth')}
                            </Typography>
                            
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                <Typography variant="body2">
                                    {translate(totpEnabled ? 'users.profile.2faEnabledText' : 'users.profile.2faDisabledText')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={handleToggle2FA}
                                    disabled={loading}
                                >
                                    {translate(totpEnabled ? 'users.profile.disable2fa' : 'users.profile.enable2fa')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
            
            {/* Activity History */}
            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Card sx={{ boxShadow: '0 12px 40px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box display="flex" alignItems="center" mb={3}>
                                <HistoryIcon sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('users.profile.activityHistory')}
                                </Typography>
                            </Box>
                            
                            {auditLoading ? (
                                <Box display="flex" justifyContent="center" py={4}>
                                    <CircularProgress />
                                </Box>
                            ) : auditLogs.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                                    {translate('users.profile.noActivity')}
                                </Typography>
                            ) : (
                                <Box>
                                    {auditLogs.map((log: any) => (
                                        <Box
                                            key={log.id}
                                            sx={{
                                                py: 2,
                                                px: 2,
                                                borderBottom: '1px solid #E0E0E0',
                                                '&:last-child': {
                                                    borderBottom: 'none',
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#F5F5F5',
                                                },
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="start" flexWrap="wrap" gap={0.5}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                                                        {translate(`audit.actions.${log.action}`, { _: log.action })}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        {log.resourceType && `${log.resourceType} `}
                                                        {log.ipAddress && `• IP: ${log.ipAddress}`}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {format(new Date(log.createdAt), 'PPp')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
            </Box>
            
            {/* 2FA Setup Dialog */}
            <Dialog
                open={show2FASetupDialog}
                onClose={() => {
                    setShow2FASetupDialog(false);
                    setVerificationCode('');
                    setVerificationCodeError('');
                    setTotpUri('');
                    setTotpSecret('');
                }}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ wordBreak: 'break-word' }}>
                    {translate('users.profile.setup2fa')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            {translate('users.profile.2faInstructions')}
                        </Typography>
                        
                        {totpUri && (
                            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ 
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white'
                                }}>
                                    <QRCodeCanvas 
                                        value={totpUri}
                                        size={200}
                                        level="M"
                                        includeMargin={false}
                                    />
                                </Box>
                            </Box>
                        )}
                        
                        <Typography variant="caption" sx={{ display: 'block', mb: 3, color: 'text.secondary', wordBreak: 'break-all' }}>
                            {translate('users.profile.manualEntry')}: <strong style={{ fontFamily: 'monospace' }}>{totpSecret}</strong>
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label={translate('users.profile.verificationCode')}
                            value={verificationCode}
                            onChange={(e) => {
                                setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                if (verificationCodeError) setVerificationCodeError('');
                            }}
                            placeholder="123456"
                            error={!!verificationCodeError}
                            helperText={verificationCodeError || undefined}
                            inputProps={{
                                maxLength: 6,
                                pattern: '[0-9]{6}',
                                inputMode: 'numeric',
                                style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem' }
                            }}
                            sx={{ maxWidth: '300px' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShow2FASetupDialog(false);
                            setVerificationCode('');
                            setVerificationCodeError('');
                            setTotpUri('');
                            setTotpSecret('');
                        }}
                        disabled={loading}
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button 
                        onClick={handleVerify2FA} 
                        variant="contained"
                        disabled={loading || verificationCode.length !== 6}
                    >
                        {loading ? <CircularProgress size={20} /> : translate('users.profile.verifyAndEnable')}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* 2FA Disable Dialog */}
            <Dialog
                open={show2FADisableDialog}
                onClose={() => {
                    setShow2FADisableDialog(false);
                    setDisablePassword('');
                    setDisablePasswordError('');
                }}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ wordBreak: 'break-word' }}>
                    {translate('users.profile.disable2fa')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            {translate('users.profile.disable2faWarning')}
                        </Alert>
                        
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {translate('users.profile.enterPasswordToDisable')}
                        </Typography>
                        
                        <TextField
                            fullWidth
                            type="password"
                            label={translate('ra.auth.password')}
                            value={disablePassword}
                            onChange={(e) => {
                                setDisablePassword(e.target.value);
                                if (disablePasswordError) setDisablePasswordError('');
                            }}
                            error={!!disablePasswordError}
                            helperText={disablePasswordError || undefined}
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShow2FADisableDialog(false);
                            setDisablePassword('');
                            setDisablePasswordError('');
                        }}
                        disabled={loading}
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button 
                        onClick={handleDisable2FA} 
                        variant="contained"
                        color="error"
                        disabled={loading || !disablePassword}
                    >
                        {loading ? <CircularProgress size={20} /> : translate('users.profile.disable2fa')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};